import React, { lazy, Suspense, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AGENTS } from '../config/agents';
import { sendAnalyticsEvent } from '../lib/analytics';
import { searchKB, type KBResult } from '../lib/kbSearch';

const EisenboardGraph = lazy(() => import('./Eisenboard/EisenboardGraph'));
import {
    buildEisenGraph,
    searchGraphNodes,
    type GraphSearchResult,
} from './Eisenboard/graph';
import {
    COLORS,
    DOMAIN_TO_STRATUM,
    clamp,
    getQuadrant,
    type Domain,
    type EisenboardViewMode,
    type Facet,
    type GraphSearchMode,
    type Quadrant,
} from './Eisenboard/model';

export type { Domain, Facet, FacetRelation, GraphRelationType, Quadrant } from './Eisenboard/model';
export { COLORS, DOMAIN_TO_STRATUM, getQuadrant } from './Eisenboard/model';

const QUADRANTS: Record<Quadrant, { label: string; x: number; y: number }> = {
	// Standard Eisenhower layout:
	// x: urgency decreases left to right (urgent = left)
	// y: importance decreases top to bottom (important = top)
	DO: { label: 'CRITICAL / DO', x: 0, y: 0 }, // Urgent + Important (top-left)
	SCHEDULE: { label: 'STRATEGIC / SCHEDULE', x: 1, y: 0 }, // Not Urgent + Important (top-right)
	DELEGATE: { label: 'NOISE / DELEGATE', x: 0, y: 1 }, // Urgent + Not Important (bottom-left)
	DELETE: { label: 'DISTRACTION / ELIMINATE', x: 1, y: 1 }, // Not Urgent + Not Important (bottom-right)
};

const SEARCH_MODE_LABELS: Record<Exclude<GraphSearchMode, 'semantic'>, string> = {
    auto: 'Auto',
    keyword: 'Keyword',
};

const MATCHED_FIELD_LABELS: Record<string, string> = {
    analysis: 'analysis',
    domain: 'domain',
    relations: 'relations',
    summary: 'summary',
    title: 'title',
};

const RELATION_TYPE_LABELS = {
    blocks: 'Blocks',
    decomposes_to: 'Decomposes To',
    related: 'Related',
    supports: 'Supports',
} as const;

interface EisenboardProps {
    facets?: Facet[];
    className?: string;
    continueTo?: string;
    problemTitle?: string;
    onInteraction?: () => void;
    onHover?: () => void;
    onCardClick?: (domain: Domain) => void;  // NEW: Called when user clicks a card
    selectedDomain?: Domain | null;
	questionCount?: number;
	maxQuestions?: number;
}

const STORAGE_KEY = 'eisenboard_facets_v1';

// --- Mock Data (Restored for UI Polish) ---
// Mock data removed. Waiting for real data.

/**
 * Confidence model: diminishing returns curve.
 */
const computeDeconstructionConfidence = (facets: Facet[]): number => {
    if (!facets || facets.length === 0) return 0;

    const total = facets.length;
    const uniqueDomains = new Set(facets.map(f => f.domain)).size;
    const domainCoverage = clamp(uniqueDomains / 5, 0, 1);

    // Tuned so that a “first complete pass” (often ~5 facets across 5 domains)
    // yields ~63% confidence (instead of ~62%) while preserving the overall curve.
    const n90 = 11.6;
    const k = Math.log(10) / n90;
    const base = 100 * (1 - Math.exp(-k * total));

    const diversity = 0.6 + 0.4 * domainCoverage;

    return clamp(Math.round(base * diversity), 0, 100);
};

const readStoredFacets = (): Facet[] | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed as Facet[];
    } catch {
        return null;
    }
};

const Eisenboard: React.FC<EisenboardProps> = ({
    facets,
    className = '',
    continueTo = '/tracker',
    problemTitle = 'Current Challenge',
    onInteraction,
    onHover,
    onCardClick,
    selectedDomain = null,
	questionCount,
	maxQuestions,
}) => {
    const navigate = useNavigate();

    // Visual Polish: Using visibleDomains (Multi-select)
    const [visibleDomains, setVisibleDomains] = useState<Domain[]>([]);
    const [viewMode, setViewMode] = useState<EisenboardViewMode>('board');
    const [searchMode, setSearchMode] = useState<GraphSearchMode>('auto');
    const [searchQuery, setSearchQuery] = useState('');
    const [graphHoveredNodeId, setGraphHoveredNodeId] = useState<string | null>(null);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    // Logic: Smart Drawer
    const [selectedFacetId, setSelectedFacetId] = useState<string | null>(null);
    const [hoveredFacetId, setHoveredFacetId] = useState<string | null>(null);
    const [enteredFacetIds, setEnteredFacetIds] = useState<Set<string>>(() => new Set());
    const selectedBubbleRef = useRef<HTMLDivElement | null>(null);
    const scheduledEnterIdsRef = useRef<Set<string>>(new Set());
    const [drawerPlacement, setDrawerPlacement] = useState<{
        align: 'left' | 'center' | 'right';
        vertical: 'down' | 'up';
        maxWidthPx: number;
    }>({ align: 'center', vertical: 'down', maxWidthPx: 320 });

    const resolvedFacets = useMemo(() => {
        // Hybrid: Props -> Storage -> Mock
        return facets ?? readStoredFacets() ?? [];
    }, [facets]);

    const deferredSearchQuery = useDeferredValue(searchQuery.trim());
    const [kbResults, setKBResults] = useState<KBResult[]>([]);
    useEffect(() => {
        if (!deferredSearchQuery) { setKBResults([]); return; }
        void searchKB(deferredSearchQuery).then(setKBResults);
    }, [deferredSearchQuery]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
        updatePreference();

        mediaQuery.addEventListener('change', updatePreference);
        return () => mediaQuery.removeEventListener('change', updatePreference);
    }, []);

    // Track entering cards for entrance animation
    useEffect(() => {
        if (resolvedFacets.length === 0) {
            scheduledEnterIdsRef.current = new Set();
            setEnteredFacetIds(new Set());
            return;
        }

        const timeouts: number[] = [];
        resolvedFacets.forEach((facet, index) => {
            if (scheduledEnterIdsRef.current.has(facet.id)) return;
            scheduledEnterIdsRef.current.add(facet.id);
            const t = window.setTimeout(() => {
                setEnteredFacetIds(prev => {
                    const next = new Set(prev);
                    next.add(facet.id);
                    return next;
                });
            }, index * 30);
            timeouts.push(t);
        });

        return () => {
            for (const t of timeouts) window.clearTimeout(t);
        };
    }, [resolvedFacets]);

    const filteredFacets = useMemo(() => {
        if (visibleDomains.length === 0) return resolvedFacets;
        return resolvedFacets.filter(f => visibleDomains.includes(f.domain));
    }, [visibleDomains, resolvedFacets]);

    useEffect(() => {
        if (!selectedFacetId) return;
        const facetStillVisible = filteredFacets.some((facet) => facet.id === selectedFacetId);
        if (!facetStillVisible) {
            setSelectedFacetId(null);
            setGraphHoveredNodeId(null);
        }
    }, [filteredFacets, selectedFacetId]);

    const confidence = useMemo(() => computeDeconstructionConfidence(resolvedFacets), [resolvedFacets]);
    const selectedFacet = useMemo(
        () => resolvedFacets.find((facet) => facet.id === selectedFacetId) ?? null,
        [resolvedFacets, selectedFacetId],
    );
    const eisenGraph = useMemo(
        () => buildEisenGraph(problemTitle, filteredFacets),
        [filteredFacets, problemTitle],
    );
    const graphSearchResults = useMemo(
        () => searchGraphNodes(eisenGraph.nodes, deferredSearchQuery, searchMode),
        [deferredSearchQuery, eisenGraph.nodes, searchMode],
    );
    const matchedGraphNodeIds = useMemo(
        () => new Set(graphSearchResults.map((result) => result.nodeId)),
        [graphSearchResults],
    );
    const selectedGraphLinks = useMemo(() => {
        if (!selectedFacetId) return [];
        const titleById = new Map(eisenGraph.nodes.map((node) => [node.id, node.title]));
        return eisenGraph.links
            .filter((link) => link.source === selectedFacetId || link.target === selectedFacetId)
            .map((link) => {
                const relatedNodeId = link.source === selectedFacetId ? link.target : link.source;
                return {
                    ...link,
                    direction: link.source === selectedFacetId ? 'outbound' : 'inbound',
                    relatedTitle: titleById.get(relatedNodeId) ?? relatedNodeId,
                };
            });
    }, [eisenGraph.links, eisenGraph.nodes, selectedFacetId]);

    // Confidence Color Logic (Locally defined or could be shared)
    const getConfidenceColor = (score: number) => {
        if (score < 40) return '#ef4444'; // Red
        if (score < 60) return '#f97316'; // Orange
        if (score < 90) return '#3b82f6'; // Blue
        if (score < 100) return '#22c55e'; // Green
        return '#39ff14'; // Neon Green (100%)
    };
    const confColor = getConfidenceColor(confidence);
    const isComplete = confidence >= 90;

    const toggleDomain = (domain: Domain, isMulti: boolean) => {
        if (isMulti) {
            setVisibleDomains(prev =>
                prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain]
            );
        } else {
            if (visibleDomains.length === 1 && visibleDomains[0] === domain) {
                setVisibleDomains([]);
            } else {
                setVisibleDomains([domain]);
            }
        }
    };

    const selectGraphFacet = (facetId: string | null, source: 'graph' | 'search') => {
        setSelectedFacetId(facetId);
        if (!facetId) {
            if (source === 'graph') {
                void sendAnalyticsEvent('eisenboard_graph_focus_cleared', {
                    visible_node_count: eisenGraph.nodes.length,
                });
            }
            return;
        }

        const facet = filteredFacets.find((item) => item.id === facetId);
        if (!facet) return;

        onInteraction?.();
        onCardClick?.(facet.domain);

        if (source === 'search') {
            void sendAnalyticsEvent('eisenboard_graph_search_result_selected', {
                facet_id: facetId,
                matched_query: deferredSearchQuery,
            });
        }

        void sendAnalyticsEvent('eisenboard_graph_node_opened', {
            facet_id: facetId,
            domain: facet.domain,
        });
    };

    const handleGraphModeChange = (nextMode: EisenboardViewMode) => {
        setViewMode(nextMode);
        if (nextMode === 'graph') {
            void sendAnalyticsEvent('eisenboard_graph_mode_opened', {
                visible_facet_count: filteredFacets.length,
                confidence,
            });
            return;
        }

        setGraphHoveredNodeId(null);
    };

    const handleGraphSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!deferredSearchQuery) return;

        void sendAnalyticsEvent('eisenboard_graph_search_submitted', {
            query: deferredSearchQuery,
            result_count: graphSearchResults.length,
            search_mode: searchMode,
        });

        const topResult = graphSearchResults[0];
        if (topResult) {
            selectGraphFacet(topResult.nodeId, 'search');
        }
    };

    // Draw placement effect
    useEffect(() => {
        if (!selectedFacetId) return;
        const bubble = selectedBubbleRef.current;
        if (!bubble) return;
        const measure = () => {
            const drawer = bubble.querySelector('[data-eisen-drawer="true"]') as HTMLElement | null;
            if (!drawer) return;
            const margin = 12;
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;
            const maxWidthPx = Math.max(200, Math.min(360, viewportW - margin * 2));
            drawer.style.maxWidth = `${maxWidthPx}px`;
            drawer.style.width = `${Math.min(256, maxWidthPx)}px`;
            const rect = drawer.getBoundingClientRect();
            let align: 'left' | 'center' | 'right' = 'center';
            if (rect.left < margin) align = 'left';
            if (rect.right > viewportW - margin) align = 'right';
            if (rect.left < margin && rect.right > viewportW - margin) align = 'center';
            let vertical: 'down' | 'up' = 'down';
            if (rect.bottom > viewportH - margin) vertical = 'up';
            setDrawerPlacement({ align, vertical, maxWidthPx });
        };
        const raf = window.requestAnimationFrame(measure);
        return () => window.cancelAnimationFrame(raf);
    }, [selectedFacetId]);

	const questionProgress = useMemo(() => {
		// When Eisenboard is rendered standalone (no chat loop), fall back to using
		// the current confidence as a proxy for "how far along" the deconstruction is.
		if (typeof questionCount !== 'number' || !Number.isFinite(questionCount)) {
			return clamp(confidence / 100, 0, 1);
		}
		const max = typeof maxQuestions === 'number' && Number.isFinite(maxQuestions) && maxQuestions > 0 ? maxQuestions : 5;
		return clamp(questionCount / max, 0, 1);
	}, [maxQuestions, questionCount, confidence]);

	const getPosition = (u: number, i: number, idForJitter: string) => {
		const confN = clamp(confidence / 100, 0, 1);
		const progressN = questionProgress;

		// Gravity model: cards spread immediately based on scores, expanding further as evidence accrues.
		// Baseline 0.55 ensures cards are well separated even on the first decomposition (question 1 of 5).
		const outwardN = clamp((0.20 + 0.80 * confN) * (0.55 + 0.45 * progressN), 0, 1);
		const easedOutward = clamp(Math.pow(outwardN, 0.85), 0, 1);

		// Normalize urgency/importance: urgent→left, important→top (standard Eisenhower).
		const x = clamp((5 - u) / 5, -1, 1); // urgency: left(high) -> right(low)
		const y = clamp((i - 5) / 5, -1, 1); // importance: up(high) -> down(low)

		const MAX_RADIUS_PCT = 42;
		let leftPct = 50 + x * MAX_RADIUS_PCT * easedOutward;
		let topPct = 50 - y * MAX_RADIUS_PCT * easedOutward;

		// Deterministic micro-jitter to reduce overlaps, strongest near center.
		const hashToUnit = (value: string): number => {
			let h = 0;
			for (let idx = 0; idx < value.length; idx++) h = (h * 31 + value.charCodeAt(idx)) | 0;
			return (h >>> 0) / 0xffffffff;
		};
		const jitterPct = 0.6 + (1 - easedOutward) * 2.0;
		const jx = hashToUnit(`${idForJitter}:x`) * 2 - 1;
		const jy = hashToUnit(`${idForJitter}:y`) * 2 - 1;
		leftPct += jx * jitterPct;
		topPct += jy * jitterPct;

		leftPct = clamp(leftPct, 6, 88);
		topPct = clamp(topPct, 6, 88);
		return { topPct, leftPct };
	};

    const getBubbleAnchor = (leftPct: number, topPct: number) => {
        const anchorX = leftPct < 20 ? 'left' : leftPct > 80 ? 'right' : 'center';
        const anchorY = topPct < 20 ? 'top' : topPct > 80 ? 'bottom' : 'center';

        const translateX = anchorX === 'left' ? '0%' : anchorX === 'right' ? '-100%' : '-50%';
        const translateY = anchorY === 'top' ? '0%' : anchorY === 'bottom' ? '-100%' : '-50%';

        return { translateX, translateY, transformOrigin: `${anchorX} ${anchorY}` as const };
    };

    const facetsByQuadrant = useMemo(() => {
        const by: Record<Quadrant, Facet[]> = { DO: [], SCHEDULE: [], DELEGATE: [], DELETE: [] };
        filteredFacets.forEach(facet => {
            by[getQuadrant(facet.urgency, facet.importance)].push(facet);
        });
        (Object.keys(by) as Quadrant[]).forEach(q => {
            by[q].sort((a, b) => (b.importance + b.urgency) - (a.importance + a.urgency));
        });
        return by;
    }, [filteredFacets]);

    // Pre-compute positions with inter-card repulsion to reduce overlaps
    const cardPositions = useMemo(() => {
        const positions = filteredFacets.map(f => {
            const pos = getPosition(f.urgency, f.importance, f.id);
            return { id: f.id, left: pos.leftPct, top: pos.topPct };
        });

        // N-body repulsion: push overlapping cards apart
		const MIN_DIST = 18; // minimum separation in percentage points (cards are ~14% wide)
		const ITERATIONS = 8;
        for (let iter = 0; iter < ITERATIONS; iter++) {
            for (let i = 0; i < positions.length; i++) {
                for (let j = i + 1; j < positions.length; j++) {
                    const dx = positions[i].left - positions[j].left;
                    const dy = positions[i].top - positions[j].top;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < MIN_DIST && dist > 0.01) {
                        const overlap = MIN_DIST - dist;
                        const force = overlap * 0.45;
                        const nx = dx / dist;
                        const ny = dy / dist;

                        positions[i].left += nx * force;
                        positions[i].top += ny * force;
                        positions[j].left -= nx * force;
                        positions[j].top -= ny * force;

                        positions[i].left = clamp(positions[i].left, 6, 88);
                        positions[i].top = clamp(positions[i].top, 6, 88);
                        positions[j].left = clamp(positions[j].left, 6, 88);
                        positions[j].top = clamp(positions[j].top, 6, 88);
                    }
                }
            }
        }

        const result: Record<string, { topPct: number; leftPct: number }> = {};
        for (const p of positions) {
            result[p.id] = { topPct: p.top, leftPct: p.left };
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredFacets, confidence, questionProgress]);

    return (
        <div
            data-testid="eisenboard"
            className={`h-full min-h-[600px] w-full bg-[#050510] text-white font-sans relative z-0 ${className}`}
        >
            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" />
            <div className="absolute inset-0 bg-grid-white/[0.05] pointer-events-none" />

            {/* --- Header / Command Bar (Responsive Layout) --- */}
            <header className="relative z-20 flex flex-col md:flex-row items-center justify-between py-4 px-4 md:px-8 bg-[#0a0a1a]/40 backdrop-blur-xl border-b border-white/5 shadow-2xl transition-all duration-500 gap-4">
                {/* Left: Title + Confidence (Mobile) */}
                <div className="w-full md:w-auto flex justify-between md:justify-start items-center gap-4">
                    <h1
                        data-testid="eisenboard-title"
                        aria-label="EISENBOARD"
                        className="text-lg md:text-xl font-bold font-mono text-lime-400"
                        style={{
                            textShadow: '1px 1px 0px #14532d',
                        }}
                    >
                        <span className="xl:hidden">EB</span>
                        <span className="hidden xl:inline tracking-[0.18em]">EISENBOARD</span>
                    </h1>

                    {/* Mobile Confidence Meter (Shown here on small screens) */}
                    {resolvedFacets.length > 0 && (
                        <div className="md:hidden flex items-center gap-2">
                            <div className="h-1 w-24 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full transition-all duration-700 ease-out shadow-[0_0_10px_currentColor]"
                                    style={{ width: `${confidence}%`, backgroundColor: confColor, color: confColor }}
                                />
                            </div>
                            <span className="text-[10px] font-bold tabular-nums" style={{ color: confColor }}>
                                {confidence}%
                            </span>
                        </div>
                    )}
                </div>

                {/* Middle: Domain Filters */}
                <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center justify-center gap-2 md:gap-4 pointer-events-auto pb-2 md:pb-0 max-w-full">
                    {([
                        { id: 'STRATEGY' as const, name: AGENTS.orchestrator.name, role: 'Strategy' },
                        { id: 'PEOPLE' as const, name: AGENTS.people.name, role: AGENTS.people.role },
                        { id: 'PROCESS' as const, name: AGENTS.process.name, role: AGENTS.process.role },
                        { id: 'TECH' as const, name: AGENTS.tech.name, role: AGENTS.tech.role },
                        { id: 'DATA' as const, name: AGENTS.data.name, role: AGENTS.data.role },
                    ] satisfies Array<{ id: Domain; name: string; role: string }>).map(agent => {
                        const isPinned = visibleDomains.includes(agent.id);
                        const dimOthers = visibleDomains.length > 0 && !isPinned;
                        const color = COLORS[agent.id];
                        const isExternallySelected = selectedDomain === agent.id;

                        return (
                            <button
                                key={agent.id}
                                data-testid={`eisenboard-domain-${agent.id.toLowerCase()}`}
                                onClick={() => toggleDomain(agent.id, false)}
                                onDoubleClick={() => toggleDomain(agent.id, true)}
                                aria-label={`${agent.name} — ${agent.role}`}
                                aria-pressed={isPinned ? 'true' : 'false'}
                                style={{
                                    borderColor: isPinned || isExternallySelected ? color : `${color}40`,
                                    boxShadow: isPinned || isExternallySelected ? `0 0 24px ${color}66` : `0 0 10px ${color}20`,
                                }}
                                className={`
                                    group flex items-center transition-all duration-300
                                    rounded-full border px-3 py-2 min-h-[44px] xl:px-4 xl:py-2
                                    ${dimOthers ? 'opacity-30 grayscale' : 'opacity-100'}
                                    ${isPinned || isExternallySelected ? 'bg-white/10' : 'bg-transparent hover:bg-white/5'}
                                `}
                                title={`${agent.name} — ${agent.role}. Double click to pin multiple.`}
                            >
                                <span
                                    className="xl:hidden font-mono text-[12px] font-black leading-none"
                                    style={{
                                        color: color,
                                        textShadow: isPinned || isExternallySelected ? `0 0 10px ${color}80` : 'none',
                                    }}
                                    aria-hidden="true"
                                >
                                    {agent.name.slice(0, 2).toUpperCase()}
                                </span>

                                <span className="hidden xl:flex flex-col items-start leading-none">
                                    <span
                                        className="text-sm font-bold transition-all"
                                        style={{
                                            color: color,
                                            textShadow: isPinned || isExternallySelected ? `0 0 10px ${color}80` : 'none',
                                        }}
                                    >
                                        {agent.name}
                                    </span>
                                    <span className="text-[9px] font-mono uppercase tracking-wider text-white/50 group-hover:text-white/80">
                                        {agent.role}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="w-full md:w-auto flex items-center justify-center">
                    <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-md">
                        {([
                            { id: 'board' as const, label: 'Board' },
                            { id: 'graph' as const, label: 'Graph' },
                        ] satisfies Array<{ id: EisenboardViewMode; label: string }>).map((mode) => {
                            const active = viewMode === mode.id;
                            return (
                                <button
                                    key={mode.id}
                                    data-testid={`eisenboard-mode-${mode.id}`}
                                    type="button"
                                    aria-pressed={active}
                                    className={`rounded-full px-4 py-2 text-xs font-semibold tracking-[0.16em] uppercase transition-all ${
                                        active
                                            ? 'bg-cyan-400/15 text-cyan-200 shadow-[0_0_24px_rgba(34,211,238,0.18)]'
                                            : 'text-slate-400 hover:text-white'
                                    }`}
                                    onClick={() => handleGraphModeChange(mode.id)}
                                >
                                    {mode.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right: Confidence Meter (Desktop) */}
                {resolvedFacets.length > 0 && (
                    <div className="hidden md:flex items-center gap-4 w-48 justify-end">
                        <div className="w-full text-right pointer-events-auto">
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span className="uppercase tracking-widest">Confidence</span>
                                <span data-testid="eisenboard-confidence" className="font-bold" style={{ color: confColor }}>{confidence}%</span>
                            </div>
                            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full transition-all duration-700 ease-out shadow-[0_0_10px_currentColor]"
                                    style={{ width: `${confidence}%`, backgroundColor: confColor, color: confColor }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {viewMode === 'graph' && (
                <div className="relative z-10 px-4 py-4 md:px-8 md:py-6">
                    {resolvedFacets.length === 0 ? (
                        <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-white/10 bg-white/[0.03] px-6 text-center backdrop-blur-xl">
                            <div className="max-w-md space-y-4">
                                <div className="text-3xl font-black uppercase tracking-tight text-white/10">
                                    No Graph Yet
                                </div>
                                <p className="text-lg text-slate-300">
                                    Once Nova starts decomposing the problem, Graph mode will turn the board into a relationship map.
                                </p>
                                <p className="text-sm text-slate-400">
                                    Start in chat, then come back here when the first cards land.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                            <div className="space-y-4">
                                {isComplete && (
                                    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100 backdrop-blur-md">
                                        <span className="font-semibold">Deconstruction complete.</span>{' '}
                                        <button
                                            type="button"
                                            onClick={() => navigate(continueTo)}
                                            className="underline hover:text-white"
                                        >
                                            Continue to Step 2 →
                                        </button>
                                    </div>
                                )}

                                <form
                                    data-testid="eisenboard-graph-search-form"
                                    onSubmit={handleGraphSearchSubmit}
                                    className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl"
                                >
                                    <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
                                        <label className="flex-1">
                                            <span className="mb-2 block text-[11px] font-mono uppercase tracking-[0.24em] text-slate-400">
                                                Search The System
                                            </span>
                                            <input
                                                data-testid="eisenboard-graph-search-input"
                                                type="search"
                                                value={searchQuery}
                                                onChange={(event) => setSearchQuery(event.target.value)}
                                                placeholder="Find a facet by title, summary, or analysis..."
                                                className="w-full rounded-2xl border border-white/10 bg-[#07101d] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-cyan-300/40"
                                            />
                                        </label>

                                        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                                            {(Object.entries(SEARCH_MODE_LABELS) as Array<[Exclude<GraphSearchMode, 'semantic'>, string]>).map(([mode, label]) => {
                                                const active = searchMode === mode;
                                                return (
                                                    <button
                                                        key={mode}
                                                        type="button"
                                                        className={`rounded-full border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                                                            active
                                                                ? 'border-cyan-300/40 bg-cyan-400/10 text-cyan-200'
                                                                : 'border-white/10 text-slate-400 hover:text-white'
                                                        }`}
                                                        onClick={() => setSearchMode(mode)}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}

                                            <button
                                                type="button"
                                                disabled
                                                className="rounded-full border border-white/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600"
                                                title="Semantic search ships in Phase 2."
                                            >
                                                Semantic
                                            </button>

                                            <button
                                                type="submit"
                                                disabled={!deferredSearchQuery || graphSearchResults.length === 0}
                                                className="rounded-full bg-cyan-500/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100 transition-colors hover:bg-cyan-500/25 disabled:cursor-not-allowed disabled:bg-white/5 disabled:text-slate-500"
                                            >
                                                Focus Best Match
                                            </button>
                                        </div>
                                    </div>

                                    <div
                                        data-testid="eisenboard-graph-results"
                                        className="mt-4 flex flex-wrap items-start gap-2"
                                    >
                                        {deferredSearchQuery ? (
                                            graphSearchResults.length > 0 ? (
                                                graphSearchResults.map((result: GraphSearchResult) => {
                                                    const isSelectedResult = selectedFacetId === result.nodeId;
                                                    return (
                                                        <button
                                                            key={result.nodeId}
                                                            type="button"
                                                            className={`rounded-2xl border px-3 py-2 text-left transition-colors ${
                                                                isSelectedResult
                                                                    ? 'border-cyan-300/40 bg-cyan-400/10'
                                                                    : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                                                            }`}
                                                            onClick={() => selectGraphFacet(result.nodeId, 'search')}
                                                        >
                                                            <div className="text-sm font-semibold text-white">{result.node.title}</div>
                                                            <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                                                                {(result.matchedFields.length > 0
                                                                    ? result.matchedFields
                                                                    : ['title']
                                                                )
                                                                    .map((field) => MATCHED_FIELD_LABELS[field] ?? field)
                                                                    .join(' · ')}
                                                            </div>
                                                        </button>
                                                    );
                                                })
                                            ) : kbResults.length > 0 ? (
                                                <div className="w-full">
                                                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                                                        No session matches — from knowledge base:
                                                    </p>
                                                    <div className="flex flex-col gap-2">
                                                        {kbResults.map((r, i) => (
                                                            <div
                                                                key={i}
                                                                className="rounded-2xl border border-white/8 bg-white/[0.025] px-4 py-3"
                                                            >
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-cyan-500/70 border border-cyan-500/20 rounded px-1.5 py-0.5">
                                                                        KB Reference
                                                                    </span>
                                                                    <span className="text-xs font-semibold text-slate-300 truncate">
                                                                        {r.source}
                                                                    </span>
                                                                </div>
                                                                <p className="text-[11px] text-slate-500 line-clamp-3 leading-relaxed">
                                                                    {r.text.replace(/^\[.*?\]\n/, '')}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-slate-500">
                                                    No graph matches yet. Try a card title, summary phrase, or agent lane.
                                                </p>
                                            )
                                        ) : (
                                            <p className="text-sm text-slate-500">
                                                Search highlights matching nodes in-place and lets you jump straight into a focused neighborhood.
                                            </p>
                                        )}
                                    </div>
                                </form>

                                <Suspense fallback={null}>
                                    <EisenboardGraph
                                        graph={eisenGraph}
                                        hoveredNodeId={graphHoveredNodeId}
                                        matchedNodeIds={matchedGraphNodeIds}
                                        onNodeHover={(nodeId) => {
                                            setGraphHoveredNodeId(nodeId);
                                            if (nodeId) onHover?.();
                                        }}
                                        onNodeSelect={(nodeId) => selectGraphFacet(nodeId, 'graph')}
                                        prefersReducedMotion={prefersReducedMotion}
                                        selectedNodeId={selectedFacetId}
                                    />
                                </Suspense>
                            </div>

                            <aside
                                data-testid="eisenboard-graph-drawer"
                                className="rounded-3xl border border-white/10 bg-[#07101d]/90 p-5 backdrop-blur-xl"
                            >
                                {selectedFacet ? (
                                    <div className="space-y-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <div
                                                    className="text-[10px] font-mono uppercase tracking-[0.22em]"
                                                    style={{ color: COLORS[selectedFacet.domain] }}
                                                >
                                                    {selectedFacet.domain}
                                                </div>
                                                <h3 className="mt-2 text-xl font-semibold text-white">
                                                    {selectedFacet.title}
                                                </h3>
                                            </div>
                                            <button
                                                type="button"
                                                className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-slate-400 hover:text-white"
                                                onClick={() => selectGraphFacet(null, 'graph')}
                                            >
                                                Clear
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 text-xs text-slate-300">
                                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3">
                                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Urgency</div>
                                                <div className="mt-2 text-2xl font-semibold text-white">{selectedFacet.urgency}</div>
                                            </div>
                                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3">
                                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">Importance</div>
                                                <div className="mt-2 text-2xl font-semibold text-white">{selectedFacet.importance}</div>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Summary</div>
                                            <p className="mt-3 text-sm leading-6 text-slate-300">
                                                {selectedFacet.summary}
                                            </p>
                                        </div>

                                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-300">AI Analysis</div>
                                            <ul className="mt-3 space-y-2">
                                                {selectedFacet.aiAnalysis.length > 0 ? (
                                                    selectedFacet.aiAnalysis.map((point, index) => (
                                                        <li
                                                            key={`${selectedFacet.id}-analysis-${index}`}
                                                            className="border-l border-white/10 pl-3 text-sm leading-6 text-slate-300"
                                                        >
                                                            {point}
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="text-sm text-slate-500">No AI analysis attached yet.</li>
                                                )}
                                            </ul>
                                        </div>

                                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Connections</div>
                                            <div className="mt-3 space-y-2">
                                                {selectedGraphLinks.length > 0 ? (
                                                    selectedGraphLinks.map((link) => (
                                                        <div
                                                            key={link.id}
                                                            className="rounded-2xl border border-white/10 bg-[#0b1523] px-3 py-3"
                                                        >
                                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                                                                {link.direction === 'outbound' ? 'Outbound' : 'Inbound'}
                                                            </div>
                                                            <div className="mt-2 text-sm font-semibold text-white">
                                                                {RELATION_TYPE_LABELS[link.type]} · {link.relatedTitle}
                                                            </div>
                                                            {link.note && (
                                                                <div className="mt-2 text-xs leading-5 text-slate-400">
                                                                    {link.note}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-sm text-slate-500">
                                                        This facet only has structural links right now. Cross-facet relations arrive in a later phase.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex h-full min-h-[420px] flex-col justify-center text-center">
                                        <div className="text-3xl font-black uppercase tracking-tight text-white/10">
                                            Focus A Node
                                        </div>
                                        <p className="mt-4 text-sm leading-6 text-slate-400">
                                            Click a facet or choose a search result to isolate its neighborhood, inspect the analysis, and follow its incoming and outgoing connections.
                                        </p>
                                    </div>
                                )}
                            </aside>
                        </div>
                    )}
                </div>
            )}

            {viewMode === 'board' && (
                <>
            {/* Mobile: Stacked Quadrants */}
            {resolvedFacets.length > 0 && (
                <div className="md:hidden relative z-10 px-4 py-6 space-y-10">
                    {(['DO', 'SCHEDULE', 'DELEGATE', 'DELETE'] as Quadrant[]).map(q => {
                        const items = facetsByQuadrant[q];
                        const quadrantLabel = QUADRANTS[q].label;

                        return (
                            <section key={q} className="space-y-3">
                                <div className="flex items-end justify-between">
                                    <div>
                                        <div className="text-[11px] font-mono tracking-[0.2em] text-white/50 uppercase">
                                            {quadrantLabel}
                                        </div>
                                        <div className="text-2xl font-black text-white/10 uppercase tracking-tight">
                                            {q}
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-slate-400 tabular-nums">
                                        {items.length}
                                    </div>
                                </div>

                                {items.length === 0 ? (
                                    <div className="text-xs text-slate-600 italic">No items yet.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {items.map(facet => {
                                            const color = COLORS[facet.domain];
                                            const isExpanded = selectedFacetId === facet.id;
                                            const isDomainSelected = selectedDomain === facet.domain;

                                            return (
                                                <button
                                                    key={facet.id}
                                                    type="button"
                                                    onClick={() => {
                                                        onInteraction?.();
                                                        setSelectedFacetId(isExpanded ? null : facet.id);
                                                        if (!isExpanded) onCardClick?.(facet.domain);
                                                    }}
                                                    className="w-full text-left rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors px-4 py-3 min-h-[44px]"
                                                    style={{
                                                        borderColor: isExpanded || isDomainSelected ? `${color}aa` : `${color}40`,
                                                        boxShadow: isExpanded
                                                            ? `0 0 24px ${color}66`
                                                            : isDomainSelected
                                                              ? `0 0 16px ${color}50`
                                                              : 'none',
                                                    }}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                                                                    {facet.domain}
                                                                </div>
                                                                {DOMAIN_TO_STRATUM[facet.domain] && (
                                                                    <div className={`text-[9px] font-mono ${DOMAIN_TO_STRATUM[facet.domain].colorClass} opacity-60`}>
                                                                        {DOMAIN_TO_STRATUM[facet.domain].short}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-sm font-semibold text-white break-words">
                                                                {facet.title}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end text-[10px] text-slate-400 tabular-nums shrink-0">
                                                            <span>U {facet.urgency}</span>
                                                            <span>I {facet.importance}</span>
                                                        </div>
                                                    </div>

                                                    {isExpanded && (
                                                        <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                                                            <p className="text-xs text-slate-400 italic">
                                                                "{facet.summary}"
                                                            </p>
                                                            <div>
                                                                <div className="text-[10px] text-cyan-300 font-bold uppercase tracking-widest mb-1">
                                                                    AI Analysis
                                                                </div>
                                                                <ul className="space-y-1">
                                                                    {facet.aiAnalysis.slice(0, 5).map((point, i) => (
                                                                        <li key={i} className="text-xs text-slate-300 leading-snug pl-3 border-l border-white/10">
                                                                            {point}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>
            )}

            {/* --- Axis Labels (VISUAL POLISH) --- */}
            <div className="hidden md:flex absolute top-24 left-12 right-0 justify-between px-12 text-[9px] font-mono tracking-[0.2em] text-amber-500/60 uppercase pointer-events-none select-none z-0">
                <span className="flex items-center gap-2">← Less Critical</span>
                <span className="flex items-center gap-2">More Critical →</span>
            </div>
            <div className="hidden md:flex absolute top-32 bottom-0 left-0 w-12 flex-col justify-between py-12 items-center text-[9px] font-mono tracking-[0.2em] text-red-400/50 uppercase pointer-events-none select-none z-0">
                <div className="flex items-center gap-2 -rotate-90 origin-center translate-y-8 whitespace-nowrap">More Urgent →</div>
                <div className="flex items-center gap-2 -rotate-90 origin-center -translate-y-8 whitespace-nowrap">← Less Urgent</div>
            </div>

            {/* Completion Banner (INCOMING LOGIC) */}
            {isComplete && (
                <div className="absolute top-16 left-0 right-0 z-40 flex justify-center pointer-events-none">
                    <div className="pointer-events-auto mt-3 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30 backdrop-blur-md shadow-xl">
                        <div className="text-[11px] text-emerald-200 font-semibold tracking-wide flex items-center gap-2">
                            Deconstruction complete.
                            <button onClick={() => navigate(continueTo)} className="underline hover:text-white">Continue to Step 2 →</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty State Guidance */}
            {resolvedFacets.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center max-w-md px-6">
                        <div className="text-4xl font-black text-white/10 mb-4 uppercase tracking-tight">Ready to Co-Think</div>
                        <p className="text-lg text-slate-300 mb-6">
                            Chat with <span className="font-bold text-purple-400">Nova</span> on the left to start breaking down your challenge together.
                        </p>
                        <p className="text-sm text-slate-400">
                            Your ideas will appear here as cards, mapped by urgency and importance. Ask follow-ups to refine.
                        </p>
                    </div>
                </div>
            )}

            {/* --- The Matrix --- */}
            <div className="hidden md:flex absolute inset-0 pt-32 pl-12 flex-wrap">
                {/* Quadrant Dividers */}
                <div className="absolute inset-0 flex">
                    <div className="w-1/2 h-full border-r border-dashed border-white/10" />
                </div>
                <div className="absolute inset-0 flex flex-col">
                    <div className="h-1/2 w-full border-b border-dashed border-white/10" />
                </div>
                {/* Quadrant Labels */}
                {Object.entries(QUADRANTS).map(([key, config]) => (
                    <div
                        key={key}
                        className="absolute text-[30px] md:text-[60px] font-black text-white/[0.03] pointer-events-none select-none uppercase tracking-tighter"
                        style={{
                            top: config.y === 0 ? '25%' : '75%',
                            left: config.x === 0 ? '10%' : '60%',
                            fontSize: 'clamp(2rem, 5vw, 4rem)' // Responsive font size
                        }}
                    >
                        {key}
                    </div>
                ))}
            </div>

            {/* --- Living Data (Bubbles) --- */}
            <div className="hidden md:block absolute inset-0 pt-32 pl-12 pointer-events-none">
                {filteredFacets.map(facet => {
                    const pos = cardPositions[facet.id] ?? getPosition(facet.urgency, facet.importance, facet.id);
                    const color = COLORS[facet.domain];
                    const isSelected = selectedFacetId === facet.id;
                    const isHovered = hoveredFacetId === facet.id;
                    const hasEntered = enteredFacetIds.has(facet.id);
                    const anchor = getBubbleAnchor(pos.leftPct, pos.topPct);
                    const isDomainSelected = selectedDomain === facet.domain;

                    // Smart Drawer Placement Styles
                    const drawerStyle: React.CSSProperties = {
                        maxWidth: `${drawerPlacement.maxWidthPx}px`,
                        width: `${Math.min(256, drawerPlacement.maxWidthPx)}px`,
                        opacity: isSelected ? 1 : 0,
                        height: isSelected ? 'auto' : '0px',
                        borderColor: color,
                        ...(drawerPlacement.vertical === 'down' ? { top: '100%', marginTop: '8px' } : { bottom: '100%', marginBottom: '8px' }),
                        ...(drawerPlacement.align === 'center' ? { left: '50%', transform: 'translateX(-50%)' } : drawerPlacement.align === 'left' ? { left: '0', transform: 'translateX(0)' } : { right: '0', left: 'auto', transform: 'translateX(0)' }),
                    };

                    return (
                        <div
                            key={facet.id}
                            data-testid="eisenboard-bubble"
                            data-facet-id={facet.id}
                            className="absolute pointer-events-none transition-[top,left] duration-700 ease-out"
                            style={{
                                top: `${pos.topPct}%`,
                                left: `${pos.leftPct}%`,
                                zIndex: isSelected ? 100 : isHovered ? 50 : isDomainSelected ? 40 : 10,
                                transform: `translate(${anchor.translateX}, ${anchor.translateY})`,
                            }}
                            ref={isSelected ? selectedBubbleRef : null}
                        >
                            <div
                                className="relative pointer-events-auto cursor-pointer group transition-[transform,opacity] duration-300 ease-out"
                                style={{
                                    opacity: hasEntered ? 1 : 0,
                                    transformOrigin: anchor.transformOrigin,
                                    transform: `scale(${hasEntered ? 1 : 0.88}) scale(${isSelected ? 2.2 : isHovered ? 1.12 : 1})`,
                                }}
                                onMouseEnter={() => {
                                    setHoveredFacetId(facet.id);
                                    onHover?.();
                                }}
                                onMouseLeave={() => setHoveredFacetId(null)}
                                onClick={() => {
                                    onInteraction?.();
                                    setSelectedFacetId(isSelected ? null : facet.id);
                                    if (!isSelected) onCardClick?.(facet.domain);
                                }}
                            >
                                <div
                                    className={`
                                        relative flex items-center justify-center
                                        w-auto h-auto min-w-[96px] min-h-[44px] px-4 py-2
                                        rounded-lg backdrop-blur-md border
                                        shadow-2xl transition-all
                                        max-w-[260px]
                                    `}
                                    style={{
                                        backgroundColor: `${color}20`,
                                        borderColor: isSelected || isDomainSelected ? color : `${color}40`,
                                        boxShadow: isSelected
                                            ? `0 0 30px ${color}60`
                                            : isDomainSelected
                                              ? `0 0 18px ${color}50`
                                              : `0 0 10px ${color}20`,
                                    }}
                                >
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white whitespace-normal break-words text-center leading-snug drop-shadow-md">
                                        {facet.title}
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-lg opacity-50" />
                                </div>

                                <div
                                    className={`
                                        absolute bg-[#0a0a1a]/95 border border-white/10 rounded-xl
                                        overflow-hidden transition-all duration-300 origin-top
                                        backdrop-blur-xl shadow-2xl
                                    `}
                                    data-eisen-drawer="true"
                                    style={drawerStyle}
                                >
                                    <div className="p-4 space-y-3">
                                        <div className="flex justify-between items-center border-b border-white/10 pb-2">
                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{facet.domain}</span>
                                            <div className="flex gap-1">
                                                <div className="w-1 h-1 rounded-full bg-white/50" />
                                                <div className="w-1 h-1 rounded-full bg-white/50" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-[8px] text-cyan-400 font-bold mb-1 uppercase flex items-center gap-1">
                                                <span className="w-1 h-1 bg-cyan-400 rounded-full animate-pulse" />
                                                AI Analysis
                                            </h4>
                                            <ul className="space-y-1">
                                                {facet.aiAnalysis.map((point, i) => (
                                                    <li key={i} className="text-[9px] text-slate-300 leading-tight pl-2 border-l border-white/10">{point}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <p className="text-[9px] text-slate-500 italic border-t border-white/5 pt-2">"{facet.summary}"</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
                </>
            )}
        </div>
    );
};

export default Eisenboard;
